import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  Check,
  CircleHelp,
  ClipboardList,
  FileText,
  Home,
  NotebookText,
  Plus,
  Save,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { EmptyState, Field } from "../components/Fields";
import { apiDownload, apiRequest, numberOrNull, stringOrNull } from "../lib/api";
import { applyListingPreviewToForm, describeListingPreview } from "../lib/listings";
import type {
  Contact,
  DocumentRecord,
  ListingPreview,
  Note,
  Offer,
  Task,
  TimelineEvent,
  TourQuestion,
  TourQuestionPhoto,
  Workspace,
  WorkspaceFitCriterion,
  WorkspaceFitSummary,
} from "../lib/types";

type TabKey = "overview" | "tourPrep" | "fit" | "offer" | "tasks" | "timeline" | "documents" | "contacts" | "notes";

const tabs: Array<{ key: TabKey; label: string; icon: React.ElementType }> = [
  { key: "overview", label: "Overview", icon: Home },
  { key: "tourPrep", label: "Tour Prep", icon: CircleHelp },
  { key: "fit", label: "Compare", icon: SlidersHorizontal },
  { key: "offer", label: "Offer", icon: FileText },
  { key: "tasks", label: "Tasks", icon: ClipboardList },
  { key: "timeline", label: "Timeline", icon: CalendarClock },
  { key: "documents", label: "Documents", icon: Upload },
  { key: "contacts", label: "Contacts", icon: Users },
  { key: "notes", label: "Notes", icon: NotebookText },
];

const workspaceStatuses = [
  "Researching",
  "Tour Scheduled",
  "Offer Due",
  "Preparing Offer",
  "Offer Submitted",
  "Under Contract",
  "Closed",
];

const timelineEventTypes = [
  "Created",
  "Tour",
  "Open House",
  "Drive-by",
  "Offer",
  "Counteroffer",
  "Inspection",
  "Appraisal",
  "Financing",
  "Deadline",
  "Price Change",
  "Status Change",
  "Contract",
  "Closing",
  "Call",
  "Email",
  "Note",
  "Other",
];

const tourQuestionStatuses = ["Open", "Answered", "Need proof", "Concern"];

const tourQuestionSections = [
  "Big Ticket Items",
  "Water / Drainage",
  "Offer Leverage",
  "Monthly Costs",
  "Neighborhood Feel",
  "Local Context",
  "Other",
];

const tourQuestionSectionAliases: Record<string, string> = {
  "Major systems": "Big Ticket Items",
  Permits: "Big Ticket Items",
  "Water and drainage": "Water / Drainage",
  "Sewer and septic": "Water / Drainage",
  "Offer leverage": "Offer Leverage",
  "Insurance and risk": "Monthly Costs",
  "HOA and ownership cost": "Monthly Costs",
  "Ownership cost": "Monthly Costs",
  Liveability: "Neighborhood Feel",
  "Local context": "Local Context",
  Custom: "Other",
  General: "Other",
};

const importanceOptions = [
  { label: "Must have", value: 5 },
  { label: "Strong preference", value: 3 },
  { label: "Nice to have", value: 1 },
];

const matchOptions = [
  { label: "Not rated", value: 0 },
  { label: "Miss", value: 1 },
  { label: "Okay", value: 3 },
  { label: "Good", value: 4 },
  { label: "Great", value: 5 },
];

function timelineEventTypeOptions(value?: string | null) {
  const current = value?.trim();
  if (current && !timelineEventTypes.includes(current)) {
    return [current, ...timelineEventTypes];
  }
  return timelineEventTypes;
}

function tourQuestionStatusOptions(value?: string | null) {
  const current = value?.trim();
  if (current && !tourQuestionStatuses.includes(current)) {
    return [current, ...tourQuestionStatuses];
  }
  return tourQuestionStatuses;
}

function tourQuestionSection(category?: string | null) {
  const current = category?.trim();
  if (!current) return "Other";
  return tourQuestionSectionAliases[current] ?? current;
}

function groupTourQuestions(questions: TourQuestion[]) {
  const groups = new Map<string, TourQuestion[]>();
  for (const question of questions) {
    const section = tourQuestionSection(question.category);
    groups.set(section, [...(groups.get(section) ?? []), question]);
  }
  return [...groups.entries()].sort(([first], [second]) => {
    const firstIndex = tourQuestionSections.indexOf(first);
    const secondIndex = tourQuestionSections.indexOf(second);
    return (firstIndex === -1 ? 999 : firstIndex) - (secondIndex === -1 ? 999 : secondIndex);
  });
}

function weightToImportance(weight: number) {
  if (weight >= 5) return 5;
  if (weight >= 3) return 3;
  return 1;
}

function ratingToMatch(rating: number) {
  if (rating >= 5) return 5;
  if (rating >= 4) return 4;
  if (rating >= 2) return 3;
  if (rating >= 1) return 1;
  return 0;
}

export function WorkspacePage() {
  const params = useParams();
  const workspaceId = Number(params.workspaceId);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");

  const workspaceQuery = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => apiRequest<Workspace>(`/api/workspaces/${workspaceId}`),
    enabled: Number.isFinite(workspaceId),
  });

  if (!Number.isFinite(workspaceId)) {
    return (
      <AppShell>
        <EmptyState title="Workspace not found" body="The property URL is not valid." />
      </AppShell>
    );
  }

  const workspace = workspaceQuery.data;

  return (
    <AppShell>
      <section className="workspace-heading">
        <div>
          <Link className="quiet-link" to="/app">
            Back to dashboard
          </Link>
          <h1>{workspace?.address ?? "Property workspace"}</h1>
          <p>
            {workspace?.status ?? "Loading"} {workspace?.offer_deadline ? `- offer due ${workspace.offer_deadline}` : ""}
          </p>
        </div>
        {workspace ? <span className="status-pill">{workspace.status}</span> : null}
      </section>

      <div className="workspace-layout">
        <aside className="workspace-tabs" aria-label="Workspace sections">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                className={activeTab === tab.key ? "tab-button active" : "tab-button"}
                type="button"
                onClick={() => setActiveTab(tab.key)}
              >
                <Icon size={17} />
                {tab.label}
              </button>
            );
          })}
        </aside>

        <section className="workspace-panel">
          {workspaceQuery.isLoading ? <div className="screen-loader inline">Loading workspace...</div> : null}
          {workspace ? (
            <>
              {activeTab === "overview" ? <OverviewTab workspace={workspace} /> : null}
              {activeTab === "tourPrep" ? <TourPrepTab workspaceId={workspaceId} /> : null}
              {activeTab === "fit" ? <FitTab workspaceId={workspaceId} /> : null}
              {activeTab === "offer" ? <OfferTab workspaceId={workspaceId} /> : null}
              {activeTab === "tasks" ? <TasksTab workspaceId={workspaceId} /> : null}
              {activeTab === "timeline" ? <TimelineTab workspaceId={workspaceId} /> : null}
              {activeTab === "documents" ? <DocumentsTab workspaceId={workspaceId} /> : null}
              {activeTab === "contacts" ? <ContactsTab workspaceId={workspaceId} /> : null}
              {activeTab === "notes" ? <NotesTab workspaceId={workspaceId} /> : null}
            </>
          ) : null}
        </section>
      </div>
    </AppShell>
  );
}

function OverviewTab({ workspace }: { workspace: Workspace }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [listingMessage, setListingMessage] = useState<string | null>(null);
  const overviewFormRef = useRef<HTMLFormElement>(null);
  const update = useMutation({
    mutationFn: (payload: Partial<Workspace>) =>
      apiRequest<Workspace>(`/api/workspaces/${workspace.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace", workspace.id] });
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });

  const previewListing = useMutation({
    mutationFn: (url: string) =>
      apiRequest<ListingPreview>("/api/listings/preview", {
        method: "POST",
        body: JSON.stringify({ url }),
      }),
  });

  async function handlePreviewListing() {
    const form = overviewFormRef.current;
    const url = form?.elements.namedItem("listing_url");
    const listingUrl = url instanceof HTMLInputElement ? url.value.trim() : "";
    if (!form || !listingUrl) {
      setError("Paste a Redfin or Zillow URL first");
      return;
    }
    setError(null);
    setListingMessage(null);
    try {
      const preview = await previewListing.mutateAsync(listingUrl);
      applyListingPreviewToForm(form, preview);
      setListingMessage([describeListingPreview(preview), ...preview.warnings].join(" "));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not import listing");
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    try {
      await update.mutateAsync({
        address: String(form.get("address") || ""),
        listing_url: stringOrNull(form.get("listing_url")),
        purchase_price: numberOrNull(form.get("purchase_price")),
        bedrooms: numberOrNull(form.get("bedrooms")),
        bathrooms: numberOrNull(form.get("bathrooms")),
        square_feet: numberOrNull(form.get("square_feet")),
        status: String(form.get("status") || "Researching"),
        offer_deadline: stringOrNull(form.get("offer_deadline")),
        personal_notes: stringOrNull(form.get("personal_notes")),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save overview");
    }
  }

  return (
    <Panel title="Overview" subtitle="The key facts and personal notes for this property.">
      <form ref={overviewFormRef} className="stack-form" onSubmit={handleSubmit}>
        <Field label="Address">
          <input name="address" required defaultValue={workspace.address} />
        </Field>
        <div className="listing-import-row">
          <Field label="Listing URL">
            <input name="listing_url" type="url" defaultValue={workspace.listing_url ?? ""} />
          </Field>
          <button
            className="button button-secondary"
            type="button"
            onClick={handlePreviewListing}
            disabled={previewListing.isPending}
          >
            <Sparkles size={17} />
            Refresh details
          </button>
        </div>
        {listingMessage ? <p className="form-hint">{listingMessage}</p> : null}
        <div className="form-grid">
          <Field label="List price">
            <input name="purchase_price" type="number" min="0" step="1000" defaultValue={workspace.purchase_price ?? ""} />
          </Field>
          <Field label="Bedrooms">
            <input name="bedrooms" type="number" min="0" step="1" defaultValue={workspace.bedrooms ?? ""} />
          </Field>
          <Field label="Bathrooms">
            <input name="bathrooms" type="number" min="0" step="0.5" defaultValue={workspace.bathrooms ?? ""} />
          </Field>
          <Field label="Square feet">
            <input name="square_feet" type="number" min="0" step="1" defaultValue={workspace.square_feet ?? ""} />
          </Field>
        </div>
        <div className="form-grid two">
          <Field label="Current status">
            <select name="status" defaultValue={workspace.status}>
              {workspaceStatuses.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </Field>
          <Field label="Offer deadline">
            <input name="offer_deadline" type="date" defaultValue={workspace.offer_deadline ?? ""} />
          </Field>
        </div>
        <Field label="Personal notes">
          <textarea name="personal_notes" rows={6} defaultValue={workspace.personal_notes ?? ""} />
        </Field>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="form-actions">
          <button className="button button-primary" type="submit" disabled={update.isPending}>
            <Save size={17} />
            Save overview
          </button>
        </div>
      </form>
    </Panel>
  );
}

function TourPrepTab({ workspaceId }: { workspaceId: number }) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const questionsQuery = useQuery({
    queryKey: ["tour-questions", workspaceId],
    queryFn: () => apiRequest<TourQuestion[]>(`/api/workspaces/${workspaceId}/tour-questions`),
  });
  const createQuestion = useMutation({
    mutationFn: (payload: Partial<TourQuestion> & { prompt: string }) =>
      apiRequest<TourQuestion>(`/api/workspaces/${workspaceId}/tour-questions`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tour-questions", workspaceId] }),
  });
  const updateQuestion = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<TourQuestion> }) =>
      apiRequest<TourQuestion>(`/api/tour-questions/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tour-questions", workspaceId] }),
  });
  const deleteQuestion = useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/api/tour-questions/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tour-questions", workspaceId] }),
  });
  const generateQuestions = useMutation({
    mutationFn: () =>
      apiRequest<TourQuestion[]>(`/api/workspaces/${workspaceId}/tour-questions/generate`, {
        method: "POST",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tour-questions", workspaceId] }),
  });
  const createTask = useMutation({
    mutationFn: (id: number) => apiRequest<Task>(`/api/tour-questions/${id}/task`, { method: "POST" }),
    onSuccess: () => {
      setMessage("Task created from question.");
      queryClient.invalidateQueries({ queryKey: ["tasks", workspaceId] });
    },
  });

  const questions = useMemo(() => questionsQuery.data ?? [], [questionsQuery.data]);
  const questionGroups = useMemo(() => groupTourQuestions(questions), [questions]);
  const stats = useMemo(
    () => ({
      concerns: questions.filter((question) => question.status === "Concern").length,
      proof: questions.filter((question) => question.status === "Need proof").length,
      open: questions.filter((question) => question.status === "Open" || question.status === "Ask agent").length,
    }),
    [questions],
  );

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const prompt = String(form.get("prompt") || "").trim();
    if (!prompt) return;
    await createQuestion.mutateAsync({
      prompt,
      category: String(form.get("category") || "Other"),
      priority: "Medium",
      status: "Open",
      source: "Custom",
      sort_order: questions.length * 10 + 100,
    });
    event.currentTarget.reset();
  }

  return (
    <Panel title="Tour Prep" subtitle="Questions and proof points to carry into a showing, walkthrough, or agent tour.">
      <div className="prep-summary">
        <div>
          <strong>{questions.length}</strong>
          <span>Questions</span>
        </div>
        <div>
          <strong>{stats.open}</strong>
          <span>Open</span>
        </div>
        <div>
          <strong>{stats.proof}</strong>
          <span>Need proof</span>
        </div>
        <div>
          <strong>{stats.concerns}</strong>
          <span>Concerns</span>
        </div>
        <button
          className="button button-secondary"
          type="button"
          onClick={() => generateQuestions.mutate()}
          disabled={generateQuestions.isPending}
        >
          <Sparkles size={17} />
          Add suggested
        </button>
      </div>
      <form className="inline-create tour-question-create" onSubmit={handleCreate}>
        <input name="prompt" placeholder="Add a question" required />
        <select name="category" defaultValue="Other">
          {tourQuestionSections.map((section) => (
            <option key={section}>{section}</option>
          ))}
        </select>
        <button className="button button-primary" type="submit" disabled={createQuestion.isPending}>
          <Plus size={17} />
          Add
        </button>
      </form>
      {message ? <p className="form-hint">{message}</p> : null}
      {questionsQuery.isLoading ? <div className="screen-loader inline">Loading tour prep...</div> : null}
      <div className="tour-question-list">
        {questionGroups.map(([section, sectionQuestions]) => (
          <section className="tour-question-section" key={section}>
            <h3>{section}</h3>
            <div className="tour-question-section-list">
              {sectionQuestions.map((question) => (
                <EditableTourQuestion
                  key={question.id}
                  question={question}
                  onSave={(payload) => updateQuestion.mutate({ id: question.id, payload })}
                  onDelete={() => deleteQuestion.mutate(question.id)}
                  onTask={() => createTask.mutate(question.id)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
      {!questionsQuery.isLoading && questions.length === 0 ? (
        <EmptyState title="No tour questions" body="Add suggested questions or create your own checklist for this property." />
      ) : null}
    </Panel>
  );
}

function EditableTourQuestion({
  question,
  onSave,
  onDelete,
  onTask,
}: {
  question: TourQuestion;
  onSave: (payload: Partial<TourQuestion>) => void;
  onDelete: () => void;
  onTask: () => void;
}) {
  const queryClient = useQueryClient();
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    prompt: question.prompt,
    category: question.category,
    status: question.status,
    answer: question.answer ?? "",
  });
  const photosQuery = useQuery({
    queryKey: ["tour-question-photos", question.id],
    queryFn: () => apiRequest<TourQuestionPhoto[]>(`/api/tour-questions/${question.id}/photos`),
  });
  const uploadPhoto = useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append("file", file);
      body.append("caption", tourQuestionSection(draft.category || question.category));
      return apiRequest<TourQuestionPhoto>(`/api/tour-questions/${question.id}/photos`, {
        method: "POST",
        body,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tour-question-photos", question.id] }),
  });
  const deletePhoto = useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/api/tour-question-photos/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tour-question-photos", question.id] }),
  });

  async function handlePhotoFiles(files: FileList | null) {
    if (!files?.length) return;
    setPhotoError(null);
    try {
      for (const file of Array.from(files)) {
        await uploadPhoto.mutateAsync(file);
      }
    } catch (err) {
      setPhotoError(err instanceof Error ? err.message : "Could not upload photo");
    }
  }

  const photos = photosQuery.data ?? [];

  return (
    <article className={`tour-question-card status-${draft.status.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="tour-question-topline">
        <textarea
          value={draft.prompt}
          onChange={(event) => setDraft({ ...draft, prompt: event.target.value })}
          rows={2}
        />
        <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>
          {tourQuestionStatusOptions(question.status).map((status) => (
            <option key={status}>{status}</option>
          ))}
        </select>
      </div>
      <textarea
        value={draft.answer}
        onChange={(event) => setDraft({ ...draft, answer: event.target.value })}
        placeholder="Answer, proof needed, or notes from the tour"
        rows={3}
      />
      {photos.length > 0 ? (
        <div className="tour-photo-grid">
          {photos.map((photo) => (
            <TourQuestionPhotoThumb
              key={photo.id}
              photo={photo}
              onDelete={() => deletePhoto.mutate(photo.id)}
            />
          ))}
        </div>
      ) : null}
      {photoError ? <p className="form-error">{photoError}</p> : null}
      <div className="item-actions">
        <button
          className="button button-secondary"
          type="button"
          onClick={() =>
            onSave({
              prompt: draft.prompt.trim() || question.prompt,
              status: draft.status,
              answer: draft.answer.trim() || null,
            })
          }
        >
          <Save size={17} />
          Save
        </button>
        <button className="button button-secondary" type="button" onClick={onTask}>
          <ClipboardList size={17} />
          Task
        </button>
        <label className="button button-secondary photo-upload-label" aria-disabled={uploadPhoto.isPending}>
          <Upload size={17} />
          Photos
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={async (event) => {
              await handlePhotoFiles(event.currentTarget.files);
              event.currentTarget.value = "";
            }}
          />
        </label>
        <button className="icon-button danger" type="button" onClick={onDelete} aria-label="Delete question">
          <Trash2 size={17} />
        </button>
      </div>
    </article>
  );
}

function TourQuestionPhotoThumb({
  photo,
  onDelete,
}: {
  photo: TourQuestionPhoto;
  onDelete: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    apiDownload(`/api/tour-question-photos/${photo.id}/download`)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (active) {
          setSrc(objectUrl);
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      })
      .catch(() => {
        if (active) setSrc(null);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo.id]);

  return (
    <article className="tour-photo-thumb">
      {src ? <img src={src} alt={photo.caption || photo.filename} /> : <div className="tour-photo-placeholder">Photo</div>}
      <button className="icon-button danger" type="button" onClick={onDelete} aria-label="Delete photo">
        <Trash2 size={15} />
      </button>
    </article>
  );
}

function FitTab({ workspaceId }: { workspaceId: number }) {
  const queryClient = useQueryClient();
  const fitQuery = useQuery({
    queryKey: ["fit", workspaceId],
    queryFn: () => apiRequest<WorkspaceFitSummary>(`/api/workspaces/${workspaceId}/fit`),
  });
  const createCriterion = useMutation({
    mutationFn: (payload: Partial<WorkspaceFitCriterion> & { label: string }) =>
      apiRequest<WorkspaceFitCriterion>(`/api/workspaces/${workspaceId}/fit`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["fit", workspaceId] }),
  });
  const updateCriterion = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<WorkspaceFitCriterion> }) =>
      apiRequest<WorkspaceFitCriterion>(`/api/fit/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["fit", workspaceId] }),
  });
  const deleteCriterion = useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/api/fit/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["fit", workspaceId] }),
  });
  const generateCriteria = useMutation({
    mutationFn: () =>
      apiRequest<WorkspaceFitSummary>(`/api/workspaces/${workspaceId}/fit/generate`, {
        method: "POST",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["fit", workspaceId] }),
  });

  const summary = fitQuery.data;
  const criteria = summary?.criteria ?? [];

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const label = String(form.get("label") || "").trim();
    if (!label) return;
    await createCriterion.mutateAsync({
      label,
      target: stringOrNull(form.get("target")),
      weight: Number(form.get("weight") || 3),
      rating: 0,
      sort_order: criteria.length * 10 + 100,
    });
    event.currentTarget.reset();
  }

  return (
    <Panel title="Compare" subtitle="A simpler way to see whether this house matches what you care about most.">
      <div className="fit-summary">
        <div>
          <strong>{summary?.fit_score === null || summary?.fit_score === undefined ? "Unrated" : `${summary.fit_score}%`}</strong>
          <span>Match score</span>
        </div>
        <div>
          <strong>
            {summary?.answered_criteria ?? 0}/{summary?.total_criteria ?? 0}
          </strong>
          <span>Rated boxes</span>
        </div>
        <button
          className="button button-secondary"
          type="button"
          onClick={() => generateCriteria.mutate()}
          disabled={generateCriteria.isPending}
        >
          <Sparkles size={17} />
          Add defaults
        </button>
      </div>
      <form className="inline-create fit-create" onSubmit={handleCreate}>
        <input name="label" placeholder="Box to check" required />
        <input name="target" placeholder="What would make this good?" />
        <select name="weight" defaultValue="3">
          {importanceOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button className="button button-primary" type="submit" disabled={createCriterion.isPending}>
          <Plus size={17} />
          Add
        </button>
      </form>
      {fitQuery.isLoading ? <div className="screen-loader inline">Loading compare...</div> : null}
      <div className="fit-list">
        {criteria.map((criterion) => (
          <EditableFitCriterion
            key={criterion.id}
            criterion={criterion}
            onSave={(payload) => updateCriterion.mutate({ id: criterion.id, payload })}
            onDelete={() => deleteCriterion.mutate(criterion.id)}
          />
        ))}
      </div>
      {!fitQuery.isLoading && criteria.length === 0 ? (
        <EmptyState title="No boxes yet" body="Add the things this property needs to get right, then rate how it compares." />
      ) : null}
    </Panel>
  );
}

function EditableFitCriterion({
  criterion,
  onSave,
  onDelete,
}: {
  criterion: WorkspaceFitCriterion;
  onSave: (payload: Partial<WorkspaceFitCriterion>) => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState({
    label: criterion.label,
    target: criterion.target ?? "",
    weight: String(weightToImportance(criterion.weight)),
    rating: String(ratingToMatch(criterion.rating)),
    notes: criterion.notes ?? "",
  });

  return (
    <article className="fit-criterion-card">
      <div className="fit-criterion-grid">
        <input value={draft.label} onChange={(event) => setDraft({ ...draft, label: event.target.value })} />
        <input
          value={draft.target}
          onChange={(event) => setDraft({ ...draft, target: event.target.value })}
          placeholder="What would make this good?"
        />
        <select value={draft.weight} onChange={(event) => setDraft({ ...draft, weight: event.target.value })}>
          {importanceOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select value={draft.rating} onChange={(event) => setDraft({ ...draft, rating: event.target.value })}>
          {matchOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <textarea
        value={draft.notes}
        onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
        placeholder="Why does it match or miss?"
        rows={2}
      />
      <div className="item-actions">
        <button
          className="button button-secondary"
          type="button"
          onClick={() =>
            onSave({
              label: draft.label.trim() || criterion.label,
              target: draft.target.trim() || null,
              weight: Number(draft.weight),
              rating: Number(draft.rating),
              notes: draft.notes.trim() || null,
            })
          }
        >
          <Save size={17} />
          Save
        </button>
        <button className="icon-button danger" type="button" onClick={onDelete} aria-label="Delete fit criterion">
          <Trash2 size={17} />
        </button>
      </div>
    </article>
  );
}

function OfferTab({ workspaceId }: { workspaceId: number }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const offerQuery = useQuery({
    queryKey: ["offer", workspaceId],
    queryFn: () => apiRequest<Offer>(`/api/workspaces/${workspaceId}/offer`),
  });
  const updateOffer = useMutation({
    mutationFn: (payload: Partial<Offer>) =>
      apiRequest<Offer>(`/api/workspaces/${workspaceId}/offer`, {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["offer", workspaceId] }),
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    try {
      await updateOffer.mutateAsync({
        offer_price: numberOrNull(form.get("offer_price")),
        earnest_money: numberOrNull(form.get("earnest_money")),
        down_payment: numberOrNull(form.get("down_payment")),
        loan_type: stringOrNull(form.get("loan_type")),
        financing_type: stringOrNull(form.get("financing_type")),
        closing_date: stringOrNull(form.get("closing_date")),
        inspection_contingency: form.get("inspection_contingency") === "on",
        appraisal_contingency: form.get("appraisal_contingency") === "on",
        financing_contingency: form.get("financing_contingency") === "on",
        status: String(form.get("status") || "Draft"),
        notes: stringOrNull(form.get("notes")),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save offer");
    }
  }

  const offer = offerQuery.data;

  return (
    <Panel title="Offer" subtitle="Draft the commercial shape of your offer before the paperwork.">
      {offerQuery.isLoading ? <div className="screen-loader inline">Loading offer...</div> : null}
      {offer ? (
        <form className="stack-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <Field label="Offer price">
              <input name="offer_price" type="number" min="0" step="1000" defaultValue={offer.offer_price ?? ""} />
            </Field>
            <Field label="Earnest money">
              <input name="earnest_money" type="number" min="0" step="500" defaultValue={offer.earnest_money ?? ""} />
            </Field>
            <Field label="Down payment">
              <input name="down_payment" type="number" min="0" step="1000" defaultValue={offer.down_payment ?? ""} />
            </Field>
            <Field label="Closing date">
              <input name="closing_date" type="date" defaultValue={offer.closing_date ?? ""} />
            </Field>
          </div>
          <div className="form-grid two">
            <Field label="Loan type">
              <input name="loan_type" placeholder="Conventional, FHA, VA..." defaultValue={offer.loan_type ?? ""} />
            </Field>
            <Field label="Cash / financing">
              <input name="financing_type" placeholder="Financing" defaultValue={offer.financing_type ?? ""} />
            </Field>
          </div>
          <Field label="Offer status">
            <select name="status" defaultValue={offer.status}>
              {["Draft", "Reviewing", "Submitted", "Countered", "Accepted", "Rejected"].map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </Field>
          <div className="toggle-row">
            <label>
              <input name="inspection_contingency" type="checkbox" defaultChecked={offer.inspection_contingency} />
              Inspection contingency
            </label>
            <label>
              <input name="appraisal_contingency" type="checkbox" defaultChecked={offer.appraisal_contingency} />
              Appraisal contingency
            </label>
            <label>
              <input name="financing_contingency" type="checkbox" defaultChecked={offer.financing_contingency} />
              Financing contingency
            </label>
          </div>
          <Field label="Notes">
            <textarea name="notes" rows={6} defaultValue={offer.notes ?? ""} />
          </Field>
          {error ? <p className="form-error">{error}</p> : null}
          <div className="form-actions">
            <button className="button button-primary" type="submit" disabled={updateOffer.isPending}>
              <Save size={17} />
              Save offer
            </button>
          </div>
        </form>
      ) : null}
    </Panel>
  );
}

function TasksTab({ workspaceId }: { workspaceId: number }) {
  const queryClient = useQueryClient();
  const tasksQuery = useQuery({
    queryKey: ["tasks", workspaceId],
    queryFn: () => apiRequest<Task[]>(`/api/workspaces/${workspaceId}/tasks`),
  });
  const createTask = useMutation({
    mutationFn: (payload: Partial<Task> & { title: string }) =>
      apiRequest<Task>(`/api/workspaces/${workspaceId}/tasks`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tasks", workspaceId] }),
  });
  const updateTask = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<Task> }) =>
      apiRequest<Task>(`/api/tasks/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tasks", workspaceId] }),
  });
  const deleteTask = useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/api/tasks/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tasks", workspaceId] }),
  });

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const title = String(form.get("title") || "").trim();
    if (!title) return;
    await createTask.mutateAsync({
      title,
      due_date: stringOrNull(form.get("due_date")),
      notes: stringOrNull(form.get("notes")),
    });
    event.currentTarget.reset();
  }

  return (
    <Panel title="Tasks" subtitle="Track the practical work that keeps the purchase moving.">
      <form className="inline-create" onSubmit={handleCreate}>
        <input name="title" placeholder="Add a task" required />
        <input name="due_date" type="date" />
        <input name="notes" placeholder="Notes" />
        <button className="button button-primary" type="submit">
          <Plus size={17} />
          Add
        </button>
      </form>
      <div className="list-stack">
        {(tasksQuery.data ?? []).map((task) => (
          <EditableTask
            key={task.id}
            task={task}
            onToggle={(completed) => updateTask.mutate({ id: task.id, payload: { completed } })}
            onSave={(payload) => updateTask.mutate({ id: task.id, payload })}
            onDelete={() => deleteTask.mutate(task.id)}
          />
        ))}
      </div>
      {!tasksQuery.isLoading && (tasksQuery.data ?? []).length === 0 ? (
        <EmptyState title="No tasks yet" body="Add the first next step, deadline, or question for this property." />
      ) : null}
    </Panel>
  );
}

function EditableTask({
  task,
  onToggle,
  onSave,
  onDelete,
}: {
  task: Task;
  onToggle: (completed: boolean) => void;
  onSave: (payload: Partial<Task>) => void;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [dueDate, setDueDate] = useState(task.due_date ?? "");
  const [notes, setNotes] = useState(task.notes ?? "");

  return (
    <article className={task.completed ? "editable-item completed" : "editable-item"}>
      <input
        className="checkbox"
        type="checkbox"
        checked={task.completed}
        onChange={(event) => onToggle(event.target.checked)}
        aria-label={`Complete ${task.title}`}
      />
      <input value={title} onChange={(event) => setTitle(event.target.value)} />
      <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
      <input value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Notes" />
      <button className="icon-button" type="button" onClick={() => onSave({ title, due_date: dueDate || null, notes: notes || null })} aria-label="Save task">
        <Check size={17} />
      </button>
      <button className="icon-button danger" type="button" onClick={onDelete} aria-label="Delete task">
        <Trash2 size={17} />
      </button>
    </article>
  );
}

function TimelineTab({ workspaceId }: { workspaceId: number }) {
  const queryClient = useQueryClient();
  const timelineQuery = useQuery({
    queryKey: ["timeline", workspaceId],
    queryFn: () => apiRequest<TimelineEvent[]>(`/api/workspaces/${workspaceId}/timeline`),
  });
  const createEvent = useMutation({
    mutationFn: (payload: Partial<TimelineEvent> & { title: string }) =>
      apiRequest<TimelineEvent>(`/api/workspaces/${workspaceId}/timeline`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["timeline", workspaceId] }),
  });
  const updateEvent = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<TimelineEvent> }) =>
      apiRequest<TimelineEvent>(`/api/timeline/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["timeline", workspaceId] }),
  });
  const deleteEvent = useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/api/timeline/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["timeline", workspaceId] }),
  });

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const title = String(form.get("title") || "").trim();
    if (!title) return;
    await createEvent.mutateAsync({
      title,
      event_date: stringOrNull(form.get("event_date")),
      event_type: stringOrNull(form.get("event_type")),
      notes: stringOrNull(form.get("notes")),
    });
    event.currentTarget.reset();
  }

  return (
    <Panel title="Timeline" subtitle="A clear record of what happened and when.">
      <form className="inline-create timeline-create" onSubmit={handleCreate}>
        <input name="title" placeholder="Event title" required />
        <input name="event_date" type="date" />
        <select name="event_type" defaultValue="">
          <option value="">Event type</option>
          {timelineEventTypes
            .filter((type) => type !== "Created")
            .map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
        </select>
        <input name="notes" placeholder="Notes" />
        <button className="button button-primary" type="submit">
          <Plus size={17} />
          Add
        </button>
      </form>
      <div className="timeline-list">
        {(timelineQuery.data ?? []).map((event) => (
          <EditableTimelineEvent
            key={event.id}
            event={event}
            onSave={(payload) => updateEvent.mutate({ id: event.id, payload })}
            onDelete={() => deleteEvent.mutate(event.id)}
          />
        ))}
      </div>
      {!timelineQuery.isLoading && (timelineQuery.data ?? []).length === 0 ? (
        <EmptyState title="No timeline events" body="Capture tours, inspections, offer updates, and closing milestones." />
      ) : null}
    </Panel>
  );
}

function EditableTimelineEvent({
  event,
  onSave,
  onDelete,
}: {
  event: TimelineEvent;
  onSave: (payload: Partial<TimelineEvent>) => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState({
    title: event.title,
    event_date: event.event_date ?? "",
    event_type: event.event_type ?? "",
    notes: event.notes ?? "",
  });

  return (
    <article className="timeline-item timeline-editable">
      <span className="timeline-dot" />
      <div className="timeline-edit-grid">
        <input value={draft.title} onChange={(change) => setDraft({ ...draft, title: change.target.value })} />
        <input
          type="date"
          value={draft.event_date}
          onChange={(change) => setDraft({ ...draft, event_date: change.target.value })}
        />
        <select
          value={draft.event_type}
          onChange={(change) => setDraft({ ...draft, event_type: change.target.value })}
        >
          <option value="">Event type</option>
          {timelineEventTypeOptions(event.event_type).map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <input
          value={draft.notes}
          onChange={(change) => setDraft({ ...draft, notes: change.target.value })}
          placeholder="Notes"
        />
      </div>
      <div className="item-actions">
        <button
          className="icon-button"
          type="button"
          onClick={() =>
            onSave({
              title: draft.title,
              event_date: draft.event_date || null,
              event_type: draft.event_type || null,
              notes: draft.notes || null,
            })
          }
          aria-label="Save timeline event"
        >
          <Check size={17} />
        </button>
        <button className="icon-button danger" type="button" onClick={onDelete} aria-label="Delete timeline event">
          <Trash2 size={17} />
        </button>
      </div>
    </article>
  );
}

function DocumentsTab({ workspaceId }: { workspaceId: number }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const documentsQuery = useQuery({
    queryKey: ["documents", workspaceId],
    queryFn: () => apiRequest<DocumentRecord[]>(`/api/workspaces/${workspaceId}/documents`),
  });
  const uploadDocument = useMutation({
    mutationFn: (payload: FormData) =>
      apiRequest<DocumentRecord>(`/api/workspaces/${workspaceId}/documents`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["documents", workspaceId] }),
  });
  const deleteDocument = useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/api/documents/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["documents", workspaceId] }),
  });

  async function handleUpload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    if (!form.get("file")) {
      setError("Choose a file to upload");
      return;
    }
    try {
      await uploadDocument.mutateAsync(form);
      event.currentTarget.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    }
  }

  async function handleDownload(document: DocumentRecord) {
    const blob = await apiDownload(`/api/documents/${document.id}/download`);
    const href = URL.createObjectURL(blob);
    const anchor = window.document.createElement("a");
    anchor.href = href;
    anchor.download = document.filename;
    anchor.click();
    URL.revokeObjectURL(href);
  }

  return (
    <Panel title="Documents" subtitle="Store disclosures, reports, images, and offer files beside the property.">
      <form className="document-upload" onSubmit={handleUpload}>
        <input name="file" type="file" accept="application/pdf,image/*" />
        <input name="category" placeholder="Category" />
        <input name="notes" placeholder="Notes" />
        <button className="button button-primary" type="submit" disabled={uploadDocument.isPending}>
          <Upload size={17} />
          Upload
        </button>
      </form>
      {error ? <p className="form-error">{error}</p> : null}
      <div className="document-grid">
        {(documentsQuery.data ?? []).map((document) => (
          <article className="document-card" key={document.id}>
            <FileText size={22} />
            <div>
              <strong>{document.filename}</strong>
              <p>
                {document.category || "Uncategorized"} - {formatBytes(document.size_bytes)}
              </p>
              {document.notes ? <p>{document.notes}</p> : null}
            </div>
            <div className="card-actions">
              <button className="button button-secondary" type="button" onClick={() => handleDownload(document)}>
                Download
              </button>
              <button className="icon-button danger" type="button" onClick={() => deleteDocument.mutate(document.id)} aria-label="Delete document">
                <Trash2 size={17} />
              </button>
            </div>
          </article>
        ))}
      </div>
      {!documentsQuery.isLoading && (documentsQuery.data ?? []).length === 0 ? (
        <EmptyState title="No documents" body="Upload disclosures, inspections, images, preapproval letters, or offer files." />
      ) : null}
    </Panel>
  );
}

function ContactsTab({ workspaceId }: { workspaceId: number }) {
  const queryClient = useQueryClient();
  const contactsQuery = useQuery({
    queryKey: ["contacts", workspaceId],
    queryFn: () => apiRequest<Contact[]>(`/api/workspaces/${workspaceId}/contacts`),
  });
  const createContact = useMutation({
    mutationFn: (payload: Partial<Contact> & { name: string }) =>
      apiRequest<Contact>(`/api/workspaces/${workspaceId}/contacts`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contacts", workspaceId] }),
  });
  const updateContact = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<Contact> }) =>
      apiRequest<Contact>(`/api/contacts/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contacts", workspaceId] }),
  });
  const deleteContact = useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/api/contacts/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contacts", workspaceId] }),
  });

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") || "").trim();
    if (!name) return;
    await createContact.mutateAsync({
      name,
      role: stringOrNull(form.get("role")),
      company: stringOrNull(form.get("company")),
      email: stringOrNull(form.get("email")),
      phone: stringOrNull(form.get("phone")),
      notes: stringOrNull(form.get("notes")),
    });
    event.currentTarget.reset();
  }

  return (
    <Panel title="Contacts" subtitle="Everyone involved, with enough context to know who to call.">
      <form className="inline-create contact-create" onSubmit={handleCreate}>
        <input name="name" placeholder="Name" required />
        <input name="role" placeholder="Role" />
        <input name="company" placeholder="Company" />
        <input name="email" type="email" placeholder="Email" />
        <input name="phone" placeholder="Phone" />
        <input name="notes" placeholder="Notes" />
        <button className="button button-primary" type="submit">
          <Plus size={17} />
          Add
        </button>
      </form>
      <div className="contact-grid">
        {(contactsQuery.data ?? []).map((contact) => (
          <EditableContact
            key={contact.id}
            contact={contact}
            onSave={(payload) => updateContact.mutate({ id: contact.id, payload })}
            onDelete={() => deleteContact.mutate(contact.id)}
          />
        ))}
      </div>
      {!contactsQuery.isLoading && (contactsQuery.data ?? []).length === 0 ? (
        <EmptyState title="No contacts" body="Add agents, lenders, inspectors, escrow, title, and insurance contacts." />
      ) : null}
    </Panel>
  );
}

function EditableContact({
  contact,
  onSave,
  onDelete,
}: {
  contact: Contact;
  onSave: (payload: Partial<Contact>) => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState({
    name: contact.name,
    role: contact.role ?? "",
    company: contact.company ?? "",
    email: contact.email ?? "",
    phone: contact.phone ?? "",
    notes: contact.notes ?? "",
  });

  return (
    <article className="contact-card">
      <div className="contact-card-header">
        <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        <button className="icon-button danger" type="button" onClick={onDelete} aria-label="Delete contact">
          <Trash2 size={17} />
        </button>
      </div>
      <input value={draft.role} onChange={(event) => setDraft({ ...draft, role: event.target.value })} placeholder="Role" />
      <input value={draft.company} onChange={(event) => setDraft({ ...draft, company: event.target.value })} placeholder="Company" />
      <input value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} placeholder="Email" />
      <input value={draft.phone} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} placeholder="Phone" />
      <textarea value={draft.notes} onChange={(event) => setDraft({ ...draft, notes: event.target.value })} placeholder="Notes" rows={3} />
      <button
        className="button button-secondary"
        type="button"
        onClick={() => onSave({ ...normalizeDraft(draft), name: draft.name.trim() || contact.name })}
      >
        <Save size={17} />
        Save
      </button>
    </article>
  );
}

function NotesTab({ workspaceId }: { workspaceId: number }) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const notesQuery = useQuery({
    queryKey: ["notes", workspaceId],
    queryFn: () => apiRequest<Note[]>(`/api/workspaces/${workspaceId}/notes`),
  });
  const createNote = useMutation({
    mutationFn: (payload: { title: string; body?: string | null }) =>
      apiRequest<Note>(`/api/workspaces/${workspaceId}/notes`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: (note) => {
      queryClient.invalidateQueries({ queryKey: ["notes", workspaceId] });
      setSelectedId(note.id);
    },
  });

  const notes = useMemo(() => notesQuery.data ?? [], [notesQuery.data]);
  const effectiveSelectedId = selectedId ?? notes[0]?.id ?? null;
  const selected = useMemo(
    () => notes.find((note) => note.id === effectiveSelectedId),
    [notes, effectiveSelectedId],
  );

  async function handleCreateNote() {
    await createNote.mutateAsync({ title: "Untitled note", body: "" });
  }

  return (
    <Panel title="Notes" subtitle="Markdown-friendly notes that autosave while you write.">
      <div className="notes-layout">
        <aside className="notes-list">
          <button className="button button-primary full-width" type="button" onClick={handleCreateNote}>
            <Plus size={17} />
            New note
          </button>
          {notes.map((note) => (
            <button
              key={note.id}
              className={effectiveSelectedId === note.id ? "note-tab active" : "note-tab"}
              type="button"
              onClick={() => setSelectedId(note.id)}
            >
              <strong>{note.title}</strong>
              <span>{new Date(note.updated_at).toLocaleDateString()}</span>
            </button>
          ))}
        </aside>
        <div className="note-editor">
          {selected ? (
            <NoteEditor
              key={selected.id}
              note={selected}
              workspaceId={workspaceId}
              onDeleted={() => setSelectedId(null)}
            />
          ) : (
            <EmptyState title="No notes yet" body="Create a note for questions, open items, and observations." />
          )}
        </div>
      </div>
    </Panel>
  );
}

function NoteEditor({
  note,
  workspaceId,
  onDeleted,
}: {
  note: Note;
  workspaceId: number;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({ title: note.title, body: note.body ?? "" });
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");

  const deleteNote = useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/api/notes/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      onDeleted();
      queryClient.invalidateQueries({ queryKey: ["notes", workspaceId] });
    },
  });

  useEffect(() => {
    const unchanged = draft.title === note.title && draft.body === (note.body ?? "");
    if (unchanged) return;

    const timer = window.setTimeout(async () => {
      await apiRequest<Note>(`/api/notes/${note.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: draft.title || "Untitled note",
          body: draft.body || null,
        }),
      });
      setSaveState("saved");
      queryClient.invalidateQueries({ queryKey: ["notes", workspaceId] });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [draft, note.body, note.id, note.title, queryClient, workspaceId]);

  return (
    <>
      <div className="note-editor-bar">
        <input
          value={draft.title}
          onChange={(event) => {
            setSaveState("saving");
            setDraft({ ...draft, title: event.target.value });
          }}
        />
        <span>{saveState === "saving" ? "Saving" : saveState === "saved" ? "Saved" : "Ready"}</span>
        <button
          className="icon-button danger"
          type="button"
          onClick={() => deleteNote.mutate(note.id)}
          aria-label="Delete note"
        >
          <Trash2 size={17} />
        </button>
      </div>
      <textarea
        className="markdown-editor"
        value={draft.body}
        onChange={(event) => {
          setSaveState("saving");
          setDraft({ ...draft, body: event.target.value });
        }}
        placeholder="Use markdown for checklists, questions, and decisions."
      />
    </>
  );
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="panel-section">
      <div className="panel-heading">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizeDraft<T extends Record<string, string>>(draft: T) {
  return Object.fromEntries(
    Object.entries(draft).map(([key, value]) => [key, value.trim() || null]),
  ) as { [K in keyof T]: string | null };
}
